import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import "./sip-calculator.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const formatINR = (amount) =>
  Number(amount.toFixed(0)).toLocaleString("en-IN");

function computeTenure(loanAmount, monthlyEMI, interestRate) {
  if (loanAmount <= 0 || monthlyEMI <= 0 || interestRate <= 0) return null;

  const monthlyInterestRate = interestRate / 12 / 100;
  const minEmi = loanAmount * monthlyInterestRate;

  // EMI must cover at least the monthly interest
  if (monthlyEMI <= minEmi) return { error: "emi_too_low", minEmi };

  const tenureMonths =
    Math.log(monthlyEMI / (monthlyEMI - loanAmount * monthlyInterestRate)) /
    Math.log(1 + monthlyInterestRate);

  if (!Number.isFinite(tenureMonths) || tenureMonths <= 0) return null;

  const totalPayment = monthlyEMI * tenureMonths;
  const totalInterest = totalPayment - loanAmount;

  return {
    tenureMonths: Number(tenureMonths.toFixed(0)),
    tenureYears: Number(tenureYearsFixed(tenureMonths)),
    totalPayment: Number(totalPayment.toFixed(0)),
    totalInterest: Number(totalInterest.toFixed(0)),
  };
}

function tenureYearsFixed(tenureMonths) {
  return (tenureMonths / 12).toFixed(2);
}

export default function TenureOptimizer() {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [monthlyEMI, setMonthlyEMI] = useState(20000);
  const [interestRate, setInterestRate] = useState(8);

  const results = useMemo(
    () => computeTenure(loanAmount, monthlyEMI, interestRate),
    [loanAmount, monthlyEMI, interestRate]
  );

  const hasResults = results && !results.error;

  const doughnutData = hasResults && {
    labels: ["Principal Amount", "Interest Paid"],
    datasets: [
      {
        data: [loanAmount, Math.max(results.totalInterest, 0)],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${formatINR(ctx.parsed)}`,
        },
      },
    },
  };

  const barData = hasResults && {
    labels: ["Principal", "Interest"],
    datasets: [
      {
        label: "Amount",
        data: [loanAmount, Math.max(results.totalInterest, 0)],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderRadius: 8,
        barThickness: 56,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `₹${formatINR(ctx.parsed.y)}`,
        },
      },
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#64748b", font: { size: 12, weight: "600" } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#eef2f5" },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          callback: (v) => `₹${formatINR(v)}`,
        },
      },
    },
  };

  return (
    <div className="sip-page">
      <section className="sip-hero">
        <div className="sip-hero-blob" aria-hidden="true" />
        <div className="sip-hero-inner">
          <p className="sip-eyebrow">TENURE OPTIMIZER</p>
          <h1 className="sip-hero-title">
            Find the Right Tenure for Your <span>Loan EMI</span>
          </h1>
          <p className="sip-hero-lead">
            Enter your loan amount, preferred monthly EMI, and interest rate to
            see how long repayment will take and what interest you will pay.
          </p>
        </div>
      </section>

      <section className="sip-calc">
        <div className="sip-calc-inner">
          <div className="sip-calc-grid">
            <div className="sip-panel">
              <h2 className="sip-panel-title">Loan Details</h2>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="ten-amount">Loan Amount</label>
                  <div className="sip-field-value sip-field-value--wide">
                    <span>₹</span>
                    <input
                      id="ten-amount"
                      type="number"
                      min={10000}
                      max={10000000}
                      step={10000}
                      value={loanAmount}
                      onChange={(e) =>
                        setLoanAmount(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={10000}
                  max={5000000}
                  step={10000}
                  value={Math.min(loanAmount, 5000000)}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  aria-label="Loan amount slider"
                />
                <div className="sip-range-ends">
                  <span>₹10,000</span>
                  <span>₹50,00,000</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="ten-emi">Monthly EMI</label>
                  <div className="sip-field-value sip-field-value--wide">
                    <span>₹</span>
                    <input
                      id="ten-emi"
                      type="number"
                      min={1000}
                      max={500000}
                      step={500}
                      value={monthlyEMI}
                      onChange={(e) =>
                        setMonthlyEMI(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1000}
                  max={100000}
                  step={500}
                  value={Math.min(monthlyEMI, 100000)}
                  onChange={(e) => setMonthlyEMI(Number(e.target.value))}
                  aria-label="Monthly EMI slider"
                />
                <div className="sip-range-ends">
                  <span>₹1,000</span>
                  <span>₹1,00,000</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="ten-rate">Interest Rate (p.a.)</label>
                  <div className="sip-field-value">
                    <input
                      id="ten-rate"
                      type="number"
                      min={1}
                      max={30}
                      step={0.1}
                      value={interestRate}
                      onChange={(e) =>
                        setInterestRate(Number(e.target.value) || 0)
                      }
                    />
                    <span>%</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={30}
                  step={0.1}
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  aria-label="Interest rate slider"
                />
                <div className="sip-range-ends">
                  <span>1%</span>
                  <span>30%</span>
                </div>
              </div>

              <div className="sip-cta-row">
                <Link to="/emi-calculator" className="sip-btn sip-btn--primary">
                  Open EMI Calculator
                </Link>
                <Link to="/loan" className="sip-btn sip-btn--ghost">
                  Explore Loan Options
                </Link>
              </div>
            </div>

            <div className="sip-panel sip-panel--results">
              <h2 className="sip-panel-title">Optimized Tenure</h2>

              {hasResults ? (
                <>
                  <div className="sip-results-top">
                    <div className="sip-donut-wrap">
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                    <div className="sip-stats">
                      <div className="sip-stat sip-stat--total">
                        <span className="sip-stat-label">Tenure</span>
                        <strong>
                          {results.tenureYears} yrs ({results.tenureMonths} mo)
                        </strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">Total Payment</span>
                        <strong>₹{formatINR(results.totalPayment)}</strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">Total Interest</span>
                        <strong className="sip-stat--teal">
                          ₹{formatINR(results.totalInterest)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <p className="sip-summary">
                    For a loan of <strong>₹{formatINR(loanAmount)}</strong> with
                    an EMI of <strong>₹{formatINR(monthlyEMI)}</strong> at{" "}
                    <strong>{interestRate}%</strong> p.a., your optimal tenure
                    is <strong>{results.tenureYears} years</strong> (
                    {results.tenureMonths} months). Total payment will be{" "}
                    <strong>₹{formatINR(results.totalPayment)}</strong>,
                    including{" "}
                    <strong>₹{formatINR(results.totalInterest)}</strong> as
                    interest.
                  </p>
                </>
              ) : results?.error === "emi_too_low" ? (
                <p className="sip-empty">
                  EMI is too low to cover interest. Increase EMI above ₹
                  {formatINR(results.minEmi)} for this loan and rate.
                </p>
              ) : (
                <p className="sip-empty">
                  Enter valid positive values to see your tenure estimate.
                </p>
              )}
            </div>
          </div>

          {hasResults && (
            <div className="sip-panel sip-panel--chart">
              <div className="sip-chart-head">
                <h2 className="sip-panel-title">Payment Breakdown</h2>
                <p>Principal vs interest over the optimized tenure</p>
              </div>
              <div className="sip-bar-wrap" style={{ height: 320 }}>
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          )}

          <div className="sip-related">
            <p>Try our other calculators</p>
            <div className="sip-related-links">
              <Link to="/emi-calculator">EMI</Link>
              <Link to="/loan-eligibility-estimator">Eligibility</Link>
              <Link to="/SipCalculator">SIP</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
